import { useEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react';

/**
 * Movimento sem dependência nova.
 *
 * §8 pede GSAP, Three.js e Framer Motion, mas nenhum está nas dependências do
 * projeto. Transição de rota, revelação, stagger de lista e contador de valores
 * — que é o movimento que o produto realmente precisa hoje — cabem em CSS e na
 * Web Animations API, já disponíveis no navegador. A recomendação de quando
 * valeria adicionar cada biblioteca está em INTEGRACAO.md.
 *
 * Regra que atravessa este arquivo: **`prefers-reduced-motion` também vale para
 * animação via JS**. A regra global em `tokens.css` só alcança CSS; a WAAPI
 * ignora `animation-duration` de folha de estilo. Aqui, quem prefere movimento
 * reduzido simplesmente não recebe animação — e nunca fica com conteúdo
 * invisível, porque o estado final é sempre o estado natural do elemento.
 */

/** Durações em ms. Curtas o bastante para não atrapalhar quem opera o dia inteiro. */
export const DURACAO = {
  instantanea: 90,
  rapida: 150,
  padrao: 240,
  revelacao: 420,
  contador: 900,
} as const;

/** Curvas de aceleração. `saida` desacelera no fim — sensação de peso controlado. */
export const CURVA = {
  saida: 'cubic-bezier(0.22, 1, 0.36, 1)',
  entrada: 'cubic-bezier(0.4, 0, 1, 1)',
  suave: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

/** Passo do stagger de lista, em ms. Acima de ~60ms a lista parece lenta. */
export const PASSO_STAGGER = 45;

/** Teto do stagger: a partir daí todos os itens entram juntos. */
const ATRASO_MAXIMO = 320;

const CONSULTA_MOVIMENTO_REDUZIDO = '(prefers-reduced-motion: reduce)';

/**
 * Leitura pontual da preferência, para código fora de componente.
 * Sem `window` (SSR, teste em Node) assume-se reduzido: não animar é o padrão seguro.
 */
export function prefereMovimentoReduzido(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(CONSULTA_MOVIMENTO_REDUZIDO).matches;
}

function assinarPreferencia(aoMudar: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const consulta = window.matchMedia(CONSULTA_MOVIMENTO_REDUZIDO);
  consulta.addEventListener('change', aoMudar);
  return () => consulta.removeEventListener('change', aoMudar);
}

/**
 * Preferência de movimento como estado reativo — o usuário pode trocar a
 * configuração do sistema com o app aberto.
 */
export function usePrefereMovimentoReduzido(): boolean {
  return useSyncExternalStore(assinarPreferencia, prefereMovimentoReduzido, () => true);
}

/** Atraso de stagger para o item de índice `indice`. */
export function atrasoDeStagger(indice: number): number {
  return Math.min(Math.max(indice, 0) * PASSO_STAGGER, ATRASO_MAXIMO);
}

/**
 * Dispara uma animação WAAPI, ou nada se o usuário prefere movimento reduzido.
 *
 * `fill: 'backwards'` de propósito: o estado final da animação é o estado
 * natural do elemento no CSS. Se a animação não rodar — preferência reduzida,
 * navegador sem WAAPI, JS que falhou — o conteúdo aparece normalmente. Nenhum
 * componente daqui esconde conteúdo por classe esperando que o JS o revele.
 */
export function animar(
  elemento: Element | null | undefined,
  quadros: Keyframe[],
  opcoes: KeyframeAnimationOptions = {},
): Animation | null {
  if (!elemento) return null;
  if (prefereMovimentoReduzido()) return null;
  if (typeof elemento.animate !== 'function') return null;

  return elemento.animate(quadros, {
    duration: DURACAO.padrao,
    easing: CURVA.saida,
    fill: 'backwards',
    ...opcoes,
  });
}

export interface OpcoesDeRevelacao {
  /** Posição na lista — gera o stagger. */
  indice?: number;
  /** Atraso extra em ms, somado ao stagger. */
  atraso?: number;
  /** De onde o conteúdo entra. `nenhuma` faz só o fade. */
  direcao?: 'baixo' | 'cima' | 'esquerda' | 'direita' | 'nenhuma';
  /** Deslocamento inicial em px. */
  distancia?: number;
  /** Revela quando o elemento entra na viewport, em vez de na montagem. */
  aoEntrarNaTela?: boolean;
  /** Dispara a revelação de novo quando este valor muda (troca de rota, refetch). */
  chave?: string | number;
  /** `false` não anima — o elemento já nasce no estado final. */
  ativo?: boolean;
}

function deslocamento(
  direcao: NonNullable<OpcoesDeRevelacao['direcao']>,
  distancia: number,
): string {
  switch (direcao) {
    case 'baixo':
      return `translate3d(0, ${distancia}px, 0)`;
    case 'cima':
      return `translate3d(0, -${distancia}px, 0)`;
    case 'esquerda':
      return `translate3d(-${distancia}px, 0, 0)`;
    case 'direita':
      return `translate3d(${distancia}px, 0, 0)`;
    case 'nenhuma':
      return 'none';
  }
}

/**
 * Ref que revela o elemento ao montar (ou ao entrar na tela).
 *
 * Genérico no tipo do elemento para servir tanto `<div>` quanto `<li>`/`<tr>`:
 * `const referencia = useRevelacao<HTMLLIElement>({ indice });`
 */
export function useRevelacao<E extends HTMLElement = HTMLDivElement>(
  opcoes: OpcoesDeRevelacao = {},
): RefObject<E> {
  const referencia = useRef<E>(null);
  const {
    indice = 0,
    atraso = 0,
    direcao = 'baixo',
    distancia = 12,
    aoEntrarNaTela = false,
    ativo = true,
    chave,
  } = opcoes;

  useEffect(() => {
    const elemento = referencia.current;
    if (elemento === null || !ativo) return;

    const quadros: Keyframe[] = [
      { opacity: 0, transform: deslocamento(direcao, distancia) },
      { opacity: 1, transform: 'none' },
    ];
    const configuracao: KeyframeAnimationOptions = {
      duration: DURACAO.revelacao,
      delay: atrasoDeStagger(indice) + atraso,
    };

    if (!aoEntrarNaTela) {
      const animacao = animar(elemento, quadros, configuracao);
      return () => animacao?.cancel();
    }

    if (typeof IntersectionObserver !== 'function') {
      animar(elemento, quadros, configuracao);
      return;
    }

    let animacao: Animation | null = null;
    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue;
          animacao = animar(entrada.target, quadros, configuracao);
          observador.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observador.observe(elemento);

    return () => {
      observador.disconnect();
      animacao?.cancel();
    };
  }, [indice, atraso, direcao, distancia, aoEntrarNaTela, ativo, chave]);

  return referencia;
}

export interface OpcoesDeContador {
  duracao?: number;
  /** Anima também na montagem, partindo de zero. */
  desdeZero?: boolean;
  /** `false` espelha o valor sem interpolar — o componente decide, não o hook. */
  ativo?: boolean;
}

/**
 * Interpola um número até `valor` — o "contador que sobe" de §8.
 *
 * Devolve o valor final imediatamente sob movimento reduzido, e também quando o
 * número não é finito: valor fiscal errado na tela é pior que ausência de
 * animação.
 */
export function useContadorAnimado(valor: number, opcoes: OpcoesDeContador = {}): number {
  const { duracao = DURACAO.contador, desdeZero = false, ativo = true } = opcoes;
  const reduzido = usePrefereMovimentoReduzido();
  const inicial = ativo && desdeZero && Number.isFinite(valor) ? 0 : valor;
  const [exibido, setExibido] = useState(inicial);
  const exibidoRef = useRef(inicial);

  useEffect(() => {
    if (
      !ativo ||
      reduzido ||
      !Number.isFinite(valor) ||
      typeof requestAnimationFrame !== 'function'
    ) {
      exibidoRef.current = valor;
      setExibido(valor);
      return;
    }

    const origem = exibidoRef.current;
    if (origem === valor) return;

    const inicio = performance.now();
    let quadro = requestAnimationFrame(function passo(agora: number): void {
      const progresso = Math.min(1, (agora - inicio) / duracao);
      // easeOutCubic: rápido no começo, assenta no fim — leitura confortável.
      const suavizado = 1 - Math.pow(1 - progresso, 3);
      const atual = origem + (valor - origem) * suavizado;
      exibidoRef.current = atual;
      setExibido(atual);
      if (progresso < 1) quadro = requestAnimationFrame(passo);
      else exibidoRef.current = valor;
    });

    return () => cancelAnimationFrame(quadro);
  }, [valor, duracao, reduzido, ativo]);

  return exibido;
}
